import React, { useState, useEffect } from "react";

import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

function ListingReviewForm({ initialData, onSubmit }) {
  const [formData, setFormData] = useState({
    title: "",
    brand: "",
    category: "",
    color: "",
    material: "",
    extracted_features: [],
    condition: "Used - Good", // Default condition
    price: "",
    description: "",
  });

  // Effect to update form when initialData is received from VLM
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev, // Keep existing user inputs like price/condition if they started typing?
        title: initialData.title || "",
        brand: initialData.brand || "",
        category: initialData.category || "",
        color: initialData.color || "",
        material: initialData.material || "",
        extracted_features: initialData.extracted_features || [],
        // Maybe don't override condition/price/description if already set?
      }));
    }
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Specific handler for Select component if needed
  const handleConditionChange = (value) => {
    setFormData((prev) => ({ ...prev, condition: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Prepare data for submission (e.g., convert price to number)
    const submissionData = {
      ...formData,
      price: parseFloat(formData.price) || 0,
      // Ensure features is an array if needed by backend
      extracted_features: Array.isArray(formData.extracted_features)
        ? formData.extracted_features
        : [],
    };
    console.log("Submitting listing:", submissionData);
    onSubmit(submissionData);
  };

  // Example condition options
  const conditionOptions = [
    "New",
    "Used - Like New",
    "Used - Excellent",
    "Used - Good",
    "Used - Fair",
  ];

  return (
    <Card className="w-full max-w-lg">
      <CardHeader>
        <CardTitle>2. Review Your Listing</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Display VLM Identified Fields (Editable) */}
          {/* ... Input/Label for title ... */}
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <Label htmlFor="brand">Brand</Label>
            <Input
              id="brand"
              name="brand"
              value={formData.brand}
              onChange={handleChange}
            />
          </div>
          <div>
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
            />
          </div>
          <div>
            <Label htmlFor="color">Color</Label>
            <Input
              id="color"
              name="color"
              value={formData.color}
              onChange={handleChange}
            />
          </div>
          <div>
            <Label htmlFor="material">Material</Label>
            <Input
              id="material"
              name="material"
              value={formData.material}
              onChange={handleChange}
            />
          </div>

          {/* Seller Inputs */}
          <div>
            <Label htmlFor="condition">Condition</Label>
            <Select
              name="condition"
              value={formData.condition}
              onValueChange={handleConditionChange}
            >
              <SelectTrigger id="condition">
                <SelectValue placeholder="Select condition" />
              </SelectTrigger>
              <SelectContent>
                {conditionOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="price">Price ($)</Label>
            <Input
              id="price"
              name="price"
              type="number"
              step="0.01"
              value={formData.price}
              onChange={handleChange}
              required
              placeholder="e.g., 25.00"
            />
          </div>
          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add any extra details, flaws, etc."
            />
          </div>

          {/* Display Extracted Features (Read-only example) */}
          {formData.extracted_features &&
            formData.extracted_features.length > 0 && (
              <div>
                <Label>Detected Features</Label>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mt-1">
                  {formData.extracted_features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

          <Button type="submit">Publish Listing</Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default ListingReviewForm;
